import { clsx } from 'clsx';
import React, { MouseEvent } from 'react';
import { Button as AriaButton, PressEvent } from 'react-aria-components';
import { Trans } from 'react-i18next';
import Icon from '~/components/data-display/Icon';
import useDataTestId from '~/hooks/useDataTestId';
import { useEllipsisTooltip } from '~/hooks/useEllipsisTooltip';
import { isDefined } from '~/types/typeGuards';
import styles from './Chip.module.scss';
import SimpleTooltip from '../SimpleTooltip';

const removeTags = (str: string) => str.replace(/<\/?[^>]+>/g, '');

interface Props extends Omit<React.HTMLAttributes<HTMLElement>, 'color' | 'onClick'> {
  background?: string;
  color?: string;
  hoverBackground?: string;
  hoverColor?: string;
  'data-testid'?: string;
  isDisabled?: boolean;
  label: string;
  maxWidth?: number | string;
  onClick?: (e: PressEvent) => void;
  Icon?: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string;
    }
  >;
  onIconClick?: (event: MouseEvent) => void;
  iconTitle?: string;
  ref?: React.Ref<HTMLElement>;
  size?: 'small' | 'medium' | 'large';
  smallRound?: boolean;
  className?: string;
}

const Chip = React.forwardRef<HTMLElement, Props>(
  (
    {
      background,
      color,
      hoverBackground,
      hoverColor,
      'data-testid': dataTestId,
      isDisabled,
      label,
      maxWidth,
      onClick,
      onIconClick,
      iconTitle,
      size = 'medium',
      smallRound,
      Icon: IconSvg,
      className,
      ...rest
    },
    ref,
  ) => {
    const dt = useDataTestId(dataTestId);
    const { ref: labelRef, title } = useEllipsisTooltip(removeTags(label));

    const isPressable = isDefined(onClick) && !isDisabled;

    const handleIconClick = (event: MouseEvent<Element>) => {
      if (isDisabled || !onIconClick) return;
      event.stopPropagation();
      onIconClick(event);
    };

    const inner = (
      <SimpleTooltip title={title} mode="div">
        <div
          className={clsx(
            styles.container,
            {
              [styles.clickable!]: isPressable,
              [styles.isDisabled!]: isDisabled,
              [styles.smallRound!]: smallRound,
            },
            styles[size],
          )}
          data-testid={dt('container')}
          style={
            {
              '--background': background,
              '--background-hovered': hoverBackground,
              '--color': color,
              '--color-hovered': hoverColor,
              maxWidth,
            } as React.CSSProperties
          }
        >
          <span className={styles.label} data-testid={dt('label')} ref={labelRef}>
            <Trans components={{ bold: <strong /> }}>{label}</Trans>
          </span>
          {IconSvg && (
            <SimpleTooltip title={iconTitle} mode="div">
              <div
                className={styles.iconContainer}
                onPointerDown={onIconClick ? (event) => event.stopPropagation() : undefined}
              >
                <Icon
                  data-testid="CancelIcon"
                  className={styles.icon}
                  component={IconSvg}
                  onClick={onIconClick ? handleIconClick : undefined}
                />
              </div>
            </SimpleTooltip>
          )}
        </div>
      </SimpleTooltip>
    );

    if (isPressable) {
      return (
        <AriaButton
          {...(rest as React.ComponentProps<typeof AriaButton>)}
          className={clsx(styles.outer, className)}
          data-testid={dt()}
          isDisabled={isDisabled}
          onPress={onClick}
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
        >
          {inner}
        </AriaButton>
      );
    }

    return (
      <div
        {...rest}
        className={clsx(styles.outer, className)}
        data-testid={dt()}
        ref={ref as React.Ref<HTMLDivElement>}
      >
        {inner}
      </div>
    );
  },
);

export default Chip;
